// Single source of truth for all legal document text. Rendered on the public
// landing pages (LegalLandingPage) and inside the app settings (LegalDetailPage).
// Legal text is intentionally English-only constants, not translation keys.

export interface LegalSection {
  heading?: string;
  paragraphs?: string[];
  bullets?: string[];
}

export interface LegalDoc {
  title: string;
  sections: LegalSection[];
}

export const TERMS_AND_CONDITIONS: LegalDoc = {
  title: 'Terms & Conditions',
  sections: [
    {
      heading: '1. Company',
      paragraphs: [
        'Culinary Cloud B.V.\nKattenburgerhof 47, 1018KD Amsterdam, The Netherlands\nKVK: 98304461\nContact: contact@culinarycloud.nl',
      ],
    },
    {
      heading: '2. Services',
      paragraphs: [
        'Culinary Cloud provides restaurant procurement and cost-tracking software on a subscription basis. Access requires an active monthly subscription.',
      ],
    },
    {
      heading: '3. Pricing & Payment',
      paragraphs: [
        'All prices include VAT unless stated otherwise. Payments are processed securely via Stripe. Subscriptions renew automatically each month until cancelled.',
      ],
    },
    {
      heading: '4. Cancellation & Refunds',
      paragraphs: [
        'You may cancel anytime via your account or by contacting us at contact@culinarycloud.nl. Access remains active until the end of the paid period. Refunds are not issued for partially used periods.',
      ],
    },
    {
      heading: '5. Use of Service',
      paragraphs: [
        'Users agree not to misuse the platform or attempt unauthorised access. We may suspend accounts that violate these terms.',
      ],
    },
    {
      heading: '6. Liability',
      paragraphs: [
        'We are not liable for indirect or consequential damages. Total liability is limited to the amount paid in the past 3 months.',
      ],
    },
    {
      heading: '7. Changes',
      paragraphs: [
        'We may update these terms with notice. Continued use implies acceptance.',
      ],
    },
    {
      heading: '8. Governing Law',
      paragraphs: [
        'Dutch law applies. Disputes fall under the court of Amsterdam.',
      ],
    },
  ],
};

export const PRIVACY_POLICY: LegalDoc = {
  title: 'Privacy Policy',
  sections: [
    {
      heading: '1. Who we are',
      paragraphs: [
        "Culinary Cloud B.V. is responsible for processing your personal data in compliance with the GDPR. This privacy policy also applies to the Hell's Kitchen Application, the Culinary Cloud - Recipes application, and the Cloud Count - Inventory application.",
      ],
    },
    {
      heading: '2. What data we collect',
      bullets: [
        'Account details (name, email, restaurant info)',
        'Billing data (via Stripe)',
        'Usage data for support and analytics (if enabled)',
      ],
    },
    {
      heading: '3. Why we collect it',
      paragraphs: [
        'To provide, bill, and support our software and comply with legal obligations.',
      ],
    },
    {
      heading: '4. Third-party processing',
      paragraphs: [
        'Payments: Stripe, Inc. – data processed under their Privacy Policy.\nWe do not sell or share personal data.',
      ],
    },
    {
      heading: '5. Data retention',
      paragraphs: [
        'We store account and billing data as long as required by law (usually 7 years) or while your subscription is active.',
      ],
    },
    {
      heading: '6. Your rights',
      paragraphs: [
        'You can request access, correction, or deletion of your data by emailing contact@culinarycloud.nl. You may also file a complaint with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens).',
      ],
    },
    {
      heading: '7. Security',
      paragraphs: [
        'We apply SSL encryption and limited data access to authorised staff only.',
      ],
    },
    {
      heading: '8. Contact',
      paragraphs: [
        'Culinary Cloud B.V.\nAddress: Kattenburgerhof 47, 1018KD Amsterdam\nEmail: contact@culinarycloud.nl',
      ],
    },
  ],
};

export const CULINARY_CLOUD_PRIVACY_POLICY: LegalDoc = {
  title: 'Privacy Policy',
  sections: [
    {
      heading: '1. Who we are',
      paragraphs: [
        'Culinary Cloud B.V. is responsible for processing your personal data in compliance with the EU General Data Protection Regulation (GDPR).',
      ],
    },
    {
      heading: '2. Confidentiality & your business data',
      paragraphs: [
        'We understand that using Culinary Cloud means entrusting us with sensitive business information, such as food cost calculations, recipes, ingredient lists, purchase prices, supplier information, and margins.',
        'We handle all data in accordance with EU GDPR regulations. This means your data is used solely to provide and improve our services to you, and no data linkable to your restaurant is shared with, sold to, or made accessible to any party outside your account. Access is restricted to authorised staff on a strict need-to-know basis.',
      ],
    },
    {
      heading: '3. What data we collect',
      bullets: [
        'Account details (name, email, restaurant info)',
        'Billing data (via Stripe)',
        'Business data you provide (recipes, ingredients, prices, suppliers, margins)',
        'Usage data for support and to improve our services',
      ],
    },
    {
      heading: '4. Why we collect it',
      paragraphs: [
        'To provide, bill, support, and improve our software, and to comply with legal obligations.',
      ],
    },
    {
      heading: '5. Third-party processing',
      paragraphs: [
        'Payments: Stripe, Inc. – data processed under their Privacy Policy.\nWe do not sell or share personal data.',
      ],
    },
    {
      heading: '6. Data retention',
      paragraphs: [
        'We store account and billing data as long as required by law or while your subscription is active.',
      ],
    },
    {
      heading: '7. Your rights',
      paragraphs: [
        'You can request access, correction, or deletion of your data by emailing contact@culinarycloud.nl. You may also file a complaint with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens).',
      ],
    },
    {
      heading: '8. Security',
      paragraphs: [
        'We apply SSL encryption and limited data access to authorised staff only.',
      ],
    },
    {
      heading: '9. Contact',
      paragraphs: [
        'Culinary Cloud B.V.\nAddress: Kattenburgerhof 47, 1018KD Amsterdam\nEmail: contact@culinarycloud.nl',
      ],
    },
  ],
};

export const COOKIES_POLICY: LegalDoc = {
  title: 'Cookie Policy',
  sections: [
    {
      paragraphs: [
        'Culinary Cloud B.V. uses a limited number of cookies on its public website.',
        'Strictly necessary technical cookies may be set by third-party providers such as Stripe to enable secure payments and session functionality.',
        "Marketing cookies are set by the LinkedIn Insight Tag on our public website. These are used to measure the performance of our LinkedIn advertising and to build audiences for retargeting. Data is processed by LinkedIn Ireland Unlimited Company under LinkedIn's Privacy Policy. You can opt out of LinkedIn advertising cookies at any time via your LinkedIn account settings.",
        'If we introduce additional cookies in the future, this policy will be updated.',
      ],
    },
  ],
};
